// src/modals/_PaiementsTabBody.jsx — Phase 2 Stripe Commit 12 (ADR-109)
//
// Body extrait depuis ResaV2Modal.jsx > PaiementsTab (refactor pattern ADR-099
// + ADR-104) — composant pur "body" : aucun chrome overlay/Esc handler.
//
// C12 pivot Checkout Sessions (ADR-109) :
//   - <CheckoutLinkBox kind='caution'|'sejour'> affiche URL session Stripe-hosted
//   - Bouton "Copier le lien" navigator.clipboard + toast feedback
//   - Bouton "Régénérer" disabled si session encore active
//   - Hide automatique si caution_status=authorized OU expires_at<now OU PI != null
//   - C12.6 fix dashboardUrl test/live mode (pi_live_xxx → /payments/, sinon /test/)
//
// Refonte v1.3 (mini-amend) — UI Stripe-centric pour Booking + Direct :
//   - Statut Caution (badge coloré + montant + Stripe Dashboard link)
//   - Statut Paiement Séjour (Direct uniquement, source='direct' OU
//     kind='pricing_block' — Booking encaisse via OTA, pas via Stripe)
//   - 4 boutons admin actions (matrice 4×3 états cf. plan v1.3 §3)
//   - Confirmation modale obligatoire avant "Libérer caution maintenant"
//
// Kind-aware résa OU pricing_block :
//   - reservation source='airbnb'  → bandeau read-only AirCover (PR #169a v1.4)
//   - reservation source='booking' → Caution UI active, Paiement Séjour off
//                                     (versement OTA, pas Stripe)
//   - reservation source='direct'  → Caution + Paiement Séjour actifs
//   - pricing_block (résa direct interne FBZ) → Caution + Paiement Séjour actifs
//
// Defensive code (argent réel) :
//   - stripe_account_id NULL côté cfg → tous boutons DISABLED + tooltip
//   - Confirmation modale "Libérer maintenant" (action destructive Stripe)
//   - Capture caution = redirection externe Stripe Dashboard (pas de capture
//     custom UI — éviter erreurs partial-capture côté FBZ)
//
// Pattern controlled : state local boutons (busy/err) hissé minimal.
// Source montants/statuts = DB row (jamais user input).

import { useState, useMemo, useEffect } from "react"
import { Ban } from "lucide-react"
import { supabase } from "../supabase"
import { DS } from "../lib/ui"
import { formatDateTimeFR } from "../lib/format"
import { cautionLinkHint } from "../lib/caution-link-hint"
import { findLogementInCfg } from "../lib/sousloc-helpers"
// Lot annulation résa payée (2e accès UI) — modale PARTAGÉE avec la vue
// Demandes directes (BookingRequestsView). Ici keyée pricingBlockId : le
// serveur résout la demande liée (booking_cancel_info) et la modale enchaîne
// booking_cancel_paid avec le request_id résolu (chemin argent inchangé).
import BookingCancelModal from "./BookingCancelModal"
import CautionMessagePreview from "./CautionMessagePreview"

// C28 — Base URL construction (FBZ public). Fallback window.location.origin.
function buildAppBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin
  }
  return ""
}

// ── Badges sémantiques ───────────────────────────────────────────────

// Caution status (5 valeurs DB possibles, cf. migration 20260526000001).
const CAUTION_BADGES = {
  pending:                   { label: "En attente",  bg: "#94a3b8", fg: "#fff" },
  requires_payment_method:   { label: "En attente",  bg: "#94a3b8", fg: "#fff" },
  authorized:                { label: "Pré-autorisée", bg: "#facc15", fg: "#1c1917" },
  captured:                  { label: "Capturée",    bg: DS.red,    fg: "#fff" },
  released:                  { label: "Libérée",     bg: DS.green,  fg: "#fff" },
  canceled:                  { label: "Annulée",     bg: "#94a3b8", fg: "#fff" },
}

// Paiement séjour status (3 valeurs DB).
const PAIEMENT_BADGES = {
  pending:    { label: "En attente", bg: "#94a3b8", fg: "#fff" },
  succeeded:  { label: "Payé",       bg: DS.green,  fg: "#fff" },
  failed:     { label: "Échec",      bg: DS.red,    fg: "#fff" },
}

function StatusBadge({ status, labelMap }) {
  const def = labelMap[status] || { label: status || "Non défini", bg: "#cbd5e1", fg: "#1c1917" }
  return (
    <span
      data-testid={`paiements-badge-${status || "none"}`}
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 12,
        background: def.bg,
        color: def.fg,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: "uppercase",
      }}
    >
      {def.label}
    </span>
  )
}

// ── Détection stripe_account_id côté UI (defensive L-1) ────────────

/**
 * Résout stripe_account_id depuis cfg pour le logement courant.
 * UI-side seulement : pas de fallback parent sous-loc (le serveur le fera
 * via getStripeAccountId si on push une action). On affiche juste un
 * warning si le log feuille n'a pas de compte Stripe configuré.
 *
 * @returns {string|null}
 */
function resolveStripeAccountIdClient(cfg, logementId) {
  if (!cfg || !logementId) return null
  const log = findLogementInCfg(cfg, logementId)
  if (!log) return null
  if (log.stripe_account_id) return log.stripe_account_id
  // Fallback parent sous-loc côté UI (best-effort, parent log peut avoir compte).
  if (log.parentLog?.stripe_account_id) return log.parentLog.stripe_account_id
  return null
}

// ── Confirmation modale "Libérer maintenant" ────────────────────────

function ConfirmReleaseModal({ open, busy, onConfirm, onCancel, cautionAmount, previewCtx, raison, onRaisonChange }) {
  if (!open) return null
  // Fix gardes release : raison obligatoire, même seuil que la capture
  // (min 20 caractères trim) — validation client miroir serveur (ADR-094).
  const raisonTrimLen = (raison || "").trim().length
  const raisonValid = raisonTrimLen >= 20
  return (
    <div
      data-testid="paiements-confirm-release-modal"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: DS.white,
          borderRadius: 12,
          padding: 24,
          maxWidth: 420,
          width: "100%",
          fontFamily: DS.fontBody,
        }}
      >
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 10 }}>
          Libérer la caution ?
        </div>
        <div style={{ fontSize: 12, color: "#444", lineHeight: 1.5, marginBottom: 14 }}>
          Cette action <strong>annule la pré-autorisation Stripe</strong> et libère immédiatement
          les fonds sur la carte du voyageur. <strong>Libérer rend l'empreinte définitivement
          irrécupérable</strong> — plus aucune capture possible sans renvoyer un nouveau lien
          caution au voyageur. À utiliser uniquement si l'état du logement est validé OK post
          check-out.
        </div>
        {/* Fix gardes release : motif obligatoire (min 20 car.), tracé en audit. */}
        <div style={{ marginBottom: 14 }}>
          <label
            htmlFor="paiements-release-raison"
            style={{ display: "block", fontSize: 12, fontWeight: 600, color: DS.navy, marginBottom: 6 }}
          >
            Motif de la libération <span style={{ color: DS.red }}>*</span>
          </label>
          <textarea
            id="paiements-release-raison"
            data-testid="paiements-release-raison"
            value={raison}
            onChange={(e) => onRaisonChange(e.target.value)}
            disabled={busy}
            rows={3}
            placeholder="Ex. : état des lieux post check-out validé OK, aucun dégât constaté"
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: 13,
              border: `1px solid ${raisonValid || raisonTrimLen === 0 ? "#dde3ec" : "#fca5a5"}`,
              borderRadius: 7,
              fontFamily: DS.fontBody,
              boxSizing: "border-box",
              resize: "vertical",
            }}
          />
          <div style={{ marginTop: 4, fontSize: 11, color: raisonValid ? "#666" : DS.red }}>
            {raisonValid
              ? "Motif enregistré dans le journal d'audit."
              : `Minimum 20 caractères (${raisonTrimLen}/20).`}
          </div>
        </div>
        {/* Aperçu #21 en direct : libération totale (0 prélevé). */}
        <CautionMessagePreview
          cautionAmount={cautionAmount}
          montantPreleve={0}
          nbNuits={previewCtx?.nbNuits}
          motif=""
          guestFirstName={previewCtx?.guestFirstName}
          logementName={previewCtx?.logementName}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid="paiements-confirm-release-cancel"
            style={{
              background: DS.cielSoft,
              color: "#666",
              border: "none",
              borderRadius: 7,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: busy ? "wait" : "pointer",
              fontFamily: DS.fontBody,
            }}
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || !raisonValid}
            data-testid="paiements-confirm-release-confirm"
            style={{
              background: DS.green,
              color: "#fff",
              border: "none",
              borderRadius: 7,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: busy ? "wait" : !raisonValid ? "not-allowed" : "pointer",
              opacity: busy || !raisonValid ? 0.6 : 1,
              fontFamily: DS.fontBody,
            }}
          >
            {busy ? "Libération…" : "Confirmer libération"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Confirmation modale "Capturer caution" (C43, double-confirm step 0+1) ─

/**
 * Modale 2 étapes pour capturer une caution Stripe en argent réel.
 *
 * Q-D Dimitri (Cap1-Cap10) actées :
 *  - Cap1 : double-confirmation OBLIGATOIRE tous montants (sécurité max argent réel)
 *  - Cap2 : raison min 20 chars (trim) — validation client + serveur
 *  - Cap4 : input libre 0 < x ≤ caution_amount_eur, pré-rempli avec max
 *  - Cap7 : optimistic refresh UI post-200 Stripe
 *
 * Step 0 = saisie : montant + raison + checkbox voyageur informé
 * Step 1 = confirmation récap : "irréversible" + bouton "Confirmer la capture"
 *
 * Props :
 *  - open : bool
 *  - step : 0 | 1
 *  - busy : bool (POST en vol)
 *  - cautionAmount : number (max EUR)
 *  - voyageurNom : string
 *  - amount, raison, voyageurNotified : state lifted parent
 *  - onAmountChange, onRaisonChange, onVoyageurNotifiedChange
 *  - onNext : passe step 0 → 1
 *  - onBack : passe step 1 → 0
 *  - onConfirm : POST final
 *  - onCancel : ferme modal
 */
// ADR-174 PR4a — Modal remboursement partiel (double-confirm argent).
function ConfirmRefundModal({
  open, step, busy, cautionAmount, amount, raison, voyageurNotified,
  onAmountChange, onRaisonChange, onVoyageurNotifiedChange, onNext, onBack, onConfirm, onCancel,
  previewCtx,
}) {
  if (!open) return null
  const amountNum = Number(amount)
  const amountValid = Number.isFinite(amountNum) && amountNum > 0 && amountNum <= cautionAmount
  const raisonTrimLen = (raison || "").trim().length
  const raisonValid = raisonTrimLen >= 10
  const step0Valid = amountValid && raisonValid && voyageurNotified === true
  return (
    <div
      data-testid="paiements-refund-modal"
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div onClick={(e) => e.stopPropagation()} className="fbz-modal-sheet" style={{ background: DS.white, borderRadius: 12, padding: 24, maxWidth: 520, width: "100%", fontFamily: DS.fontBody, maxHeight: "90vh", overflowY: "auto" }}>
        {step === 0 && (
          <div data-testid="paiements-refund-step-0">
            <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 10 }}>
              ↩️ Rembourser une partie de la caution ?
            </div>
            <div style={{ padding: "10px 12px", background: DS.cielSoft, border: `1px solid `, color: "#1e3a8a", borderRadius: 8, fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}>
              Rend de l'argent au voyageur (max {cautionAmount.toFixed(2)} €). Le remboursement peut prendre 5 à 10 jours ouvrés à apparaître sur son compte.
            </div>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="paiements-refund-amount" style={{ display: "block", fontSize: 12, fontWeight: 600, color: DS.navy, marginBottom: 6 }}>
                Montant à rendre (€) — max {cautionAmount.toFixed(2)} €
              </label>
              <input
                id="paiements-refund-amount" data-testid="paiements-refund-amount" type="number" min="0.01" max={cautionAmount} step="0.01"
                value={amount} onChange={(e) => onAmountChange(e.target.value)} disabled={busy}
                style={{ width: "100%", padding: "8px 12px", fontSize: 14, border: `1px solid ${amountValid || amount === "" ? "#dde3ec" : "#fca5a5"}`, borderRadius: 7, fontFamily: DS.fontBody, boxSizing: "border-box" }}
              />
              {!amountValid && amount !== "" && (
                <div data-testid="paiements-refund-amount-err" style={{ marginTop: 4, fontSize: 11, color: DS.red }}>
                  Montant doit être &gt; 0 et ≤ {cautionAmount.toFixed(2)} €
                </div>
              )}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label htmlFor="paiements-refund-raison" style={{ display: "block", fontSize: 12, fontWeight: 600, color: DS.navy, marginBottom: 6 }}>
                Raison du remboursement <span style={{ color: DS.red }}>*</span>
              </label>
              <textarea
                id="paiements-refund-raison" data-testid="paiements-refund-raison" value={raison} onChange={(e) => onRaisonChange(e.target.value)} disabled={busy} rows={3}
                placeholder="Ex : excédent après chiffrage du dégât (min 10 caractères)…"
                style={{ width: "100%", padding: "8px 12px", fontSize: 13, border: `1px solid ${raisonValid || raison === "" ? "#dde3ec" : "#fca5a5"}`, borderRadius: 7, fontFamily: DS.fontBody, boxSizing: "border-box", resize: "vertical" }}
              />
              <div data-testid="paiements-refund-raison-counter" style={{ marginTop: 4, fontSize: 11, color: raisonValid ? DS.green : "#666", fontWeight: raisonValid ? 700 : 400 }}>
                {raisonTrimLen}/10 caractères minimum{!raisonValid && raisonTrimLen > 0 && " (insuffisant)"}
              </div>
            </div>
            {/* Aperçu #21 en direct : le voyageur est remboursé de `amount`
                (montant libéré) ; prélevé = caution − remboursé. */}
            <CautionMessagePreview
              cautionAmount={cautionAmount}
              montantPreleve={amountValid ? cautionAmount - amountNum : 0}
              nbNuits={previewCtx?.nbNuits}
              motif={raison}
              guestFirstName={previewCtx?.guestFirstName}
              logementName={previewCtx?.logementName}
            />
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#444", cursor: busy ? "wait" : "pointer", lineHeight: 1.5 }}>
                <input type="checkbox" data-testid="paiements-refund-notified" checked={voyageurNotified} onChange={(e) => onVoyageurNotifiedChange(e.target.checked)} disabled={busy} style={{ marginTop: 2 }} />
                <span>Je confirme avoir informé le voyageur de ce remboursement.</span>
              </label>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button data-testid="paiements-refund-cancel" onClick={onCancel} disabled={busy} style={{ background: DS.cielSoft, color: "#555", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: busy ? "wait" : "pointer", fontFamily: DS.fontBody }}>
                Annuler
              </button>
              <button data-testid="paiements-refund-next" onClick={onNext} disabled={!step0Valid || busy} style={{ background: step0Valid ? DS.navy : "#cbd5e1", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: step0Valid && !busy ? "pointer" : "not-allowed", fontFamily: DS.fontBody }}>
                Continuer →
              </button>
            </div>
          </div>
        )}
        {step === 1 && (
          <div data-testid="paiements-refund-step-1">
            <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 12 }}>
              Confirmer le remboursement
            </div>
            <div style={{ padding: "12px 14px", background: DS.card, borderRadius: 8, fontSize: 13, color: "#333", marginBottom: 18, lineHeight: 1.6 }}>
              Rembourser <strong>{Number(amount).toFixed(2)} €</strong> au voyageur ?<br />
              Raison : <em>{(raison || "").trim()}</em>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button data-testid="paiements-refund-back" onClick={onBack} disabled={busy} style={{ background: DS.cielSoft, color: "#555", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: busy ? "wait" : "pointer", fontFamily: DS.fontBody }}>
                ← Retour
              </button>
              <button data-testid="paiements-refund-confirm" onClick={onConfirm} disabled={busy} style={{ background: busy ? "#cbd5e1" : DS.navy, color: "#fff", border: "none", borderRadius: 7, padding: "8px 18px", fontSize: 13, fontWeight: 700, cursor: busy ? "wait" : "pointer", fontFamily: DS.fontBody }}>
                {busy ? "Remboursement…" : "↩️ Confirmer le remboursement"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ConfirmCaptureModal({
  open,
  step,
  busy,
  cautionAmount,
  voyageurNom,
  amount,
  raison,
  voyageurNotified,
  onAmountChange,
  onRaisonChange,
  onVoyageurNotifiedChange,
  onNext,
  onBack,
  onConfirm,
  onCancel,
  previewCtx,
}) {
  if (!open) return null

  // Validations client step 0 (mirroir serveur, defense-in-depth ADR-094).
  const amountNum = Number(amount)
  const amountValid = Number.isFinite(amountNum) && amountNum > 0 && amountNum <= cautionAmount
  const raisonTrimLen = (raison || "").trim().length
  const raisonValid = raisonTrimLen >= 20
  const step0Valid = amountValid && raisonValid && voyageurNotified === true

  return (
    <div
      data-testid="paiements-capture-modal"
      onClick={onCancel}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.55)",
        zIndex: 1100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="fbz-modal-sheet"
        style={{
          background: DS.white,
          borderRadius: 12,
          padding: 24,
          maxWidth: 520,
          width: "100%",
          fontFamily: DS.fontBody,
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {step === 0 && (
          <div data-testid="paiements-capture-step-0">
            <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 10 }}>
              Capturer la caution ?
            </div>
            <div
              style={{
                padding: "10px 12px",
                background: DS.dangerBg,
                border: `1px solid 44`,
                color: "#991b1b",
                borderRadius: 8,
                fontSize: 12,
                marginBottom: 16,
                lineHeight: 1.5,
              }}
            >
              <strong>Cette action débite réellement la carte du voyageur.</strong>{" "}
              Irréversible. À utiliser uniquement après constat d'un sinistre documenté.
            </div>

            {/* Input montant */}
            <div style={{ marginBottom: 14 }}>
              <label
                htmlFor="paiements-capture-amount"
                style={{ display: "block", fontSize: 12, fontWeight: 600, color: DS.navy, marginBottom: 6 }}
              >
                Montant à capturer (€) — max {cautionAmount.toFixed(2)} €
              </label>
              <input
                id="paiements-capture-amount"
                data-testid="paiements-capture-amount"
                type="number"
                min="0.01"
                max={cautionAmount}
                step="0.01"
                value={amount}
                onChange={(e) => onAmountChange(e.target.value)}
                disabled={busy}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: 14,
                  border: `1px solid ${amountValid ? "#dde3ec" : "#fca5a5"}`,
                  borderRadius: 7,
                  fontFamily: DS.fontBody,
                  boxSizing: "border-box",
                }}
              />
              {!amountValid && amount !== "" && (
                <div
                  data-testid="paiements-capture-amount-err"
                  style={{ marginTop: 4, fontSize: 11, color: DS.red }}
                >
                  Montant doit être &gt; 0 et ≤ {cautionAmount.toFixed(2)} €
                </div>
              )}
            </div>

            {/* Textarea raison */}
            <div style={{ marginBottom: 14 }}>
              <label
                htmlFor="paiements-capture-raison"
                style={{ display: "block", fontSize: 12, fontWeight: 600, color: DS.navy, marginBottom: 6 }}
              >
                Motif du prélèvement <span style={{ color: DS.red }}>*</span>
              </label>
              <textarea
                id="paiements-capture-raison"
                data-testid="paiements-capture-raison"
                value={raison}
                onChange={(e) => onRaisonChange(e.target.value)}
                disabled={busy}
                rows={4}
                placeholder="Décrivez les dommages constatés (min 20 caractères)…"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  fontSize: 13,
                  border: `1px solid ${raisonValid || raison === "" ? "#dde3ec" : "#fca5a5"}`,
                  borderRadius: 7,
                  fontFamily: DS.fontBody,
                  boxSizing: "border-box",
                  resize: "vertical",
                }}
              />
              <div
                data-testid="paiements-capture-raison-counter"
                style={{
                  marginTop: 4,
                  fontSize: 11,
                  color: raisonValid ? DS.green : "#666",
                  fontWeight: raisonValid ? 700 : 400,
                }}
              >
                {raisonTrimLen}/20 caractères minimum
                {!raisonValid && raisonTrimLen > 0 && " (insuffisant)"}
              </div>
            </div>

            {/* Aperçu #21 en direct : prélèvement (partiel/total selon montant). */}
            <CautionMessagePreview
              cautionAmount={cautionAmount}
              montantPreleve={amountValid ? amountNum : 0}
              nbNuits={previewCtx?.nbNuits}
              motif={raison}
              guestFirstName={previewCtx?.guestFirstName}
              logementName={previewCtx?.logementName}
            />

            {/* Checkbox voyageur informé */}
            <div style={{ marginBottom: 18 }}>
              <label
                style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 12, color: "#444", cursor: busy ? "wait" : "pointer", lineHeight: 1.5 }}
              >
                <input
                  type="checkbox"
                  data-testid="paiements-capture-checkbox-voyageur"
                  checked={voyageurNotified}
                  onChange={(e) => onVoyageurNotifiedChange(e.target.checked)}
                  disabled={busy}
                  style={{ marginTop: 2 }}
                />
                <span>
                  J'ai informé le voyageur de cette capture (par email/SMS/WhatsApp,
                  avant validation finale).
                </span>
              </label>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={onCancel}
                disabled={busy}
                data-testid="paiements-capture-btn-cancel"
                style={{
                  background: DS.cielSoft,
                  color: "#666",
                  border: "none",
                  borderRadius: 7,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: busy ? "wait" : "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                Annuler
              </button>
              <button
                onClick={onNext}
                disabled={!step0Valid || busy}
                data-testid="paiements-capture-btn-next"
                style={{
                  background: step0Valid ? DS.red : "#cbd5e1",
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: step0Valid && !busy ? "pointer" : "not-allowed",
                  opacity: step0Valid ? 1 : 0.6,
                  fontFamily: DS.fontBody,
                }}
              >
                Suivant →
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="paiements-capture-step-1">
            <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 10 }}>
              Confirmer définitivement ?
            </div>
            <div
              style={{
                padding: "10px 12px",
                background: DS.dangerBg,
                border: `1px solid 44`,
                color: "#991b1b",
                borderRadius: 8,
                fontSize: 12,
                marginBottom: 16,
                lineHeight: 1.5,
              }}
            >
              <strong>Cette action est IRRÉVERSIBLE.</strong> Les fonds seront
              débités réellement sur la carte du voyageur dès validation.
            </div>

            <div
              style={{
                background: DS.card,
                border: `1px solid `,
                borderRadius: 8,
                padding: "12px 14px",
                fontSize: 12,
                lineHeight: 1.8,
                marginBottom: 18,
              }}
            >
              <div>
                <strong>Voyageur :</strong> {voyageurNom || "Voyageur"}
              </div>
              <div>
                <strong>Montant à capturer :</strong>{" "}
                <span style={{ color: DS.red, fontWeight: 700 }}>
                  {amountNum.toFixed(2)} €
                </span>
              </div>
              <div>
                <strong>Sur caution de :</strong> {cautionAmount.toFixed(2)} € pré-autorisés
              </div>
              <div style={{ marginTop: 6 }}>
                <strong>Raison :</strong>
                <div
                  style={{
                    marginTop: 4,
                    padding: "8px 10px",
                    background: DS.white,
                    border: `1px solid `,
                    borderRadius: 6,
                    fontSize: 12,
                    color: "#444",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {raison.trim()}
                </div>
              </div>
              <div style={{ marginTop: 6 }}>
                <strong>Voyageur informé :</strong>{" "}
                <span style={{ color: DS.green, fontWeight: 700 }}>oui</span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={onBack}
                disabled={busy}
                data-testid="paiements-capture-btn-back"
                style={{
                  background: DS.cielSoft,
                  color: "#666",
                  border: "none",
                  borderRadius: 7,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: busy ? "wait" : "pointer",
                  fontFamily: DS.fontBody,
                }}
              >
                ← Retour
              </button>
              <button
                onClick={onConfirm}
                disabled={busy}
                data-testid="paiements-capture-btn-confirm"
                style={{
                  background: DS.red,
                  color: "#fff",
                  border: "none",
                  borderRadius: 7,
                  padding: "8px 14px",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: busy ? "wait" : "pointer",
                  opacity: busy ? 0.6 : 1,
                  fontFamily: DS.fontBody,
                }}
              >
                {busy ? "Capture en cours…" : "Confirmer la capture"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── CheckoutLinkBox (C28 pivot Embedded ADR-109 amend) ──────────────

/**
 * Affichage URL FBZ courte /paiement/{token} + actions copier/régénérer.
 *
 * C28 pivot Embedded : URL = FBZ brandée courte (pas URL Stripe longue).
 * Le token vient de form_tokens (chargé via hook usePaymentToken).
 * La Stripe Session sera créée JIT à l'ouverture de la PaiementPage par
 * le voyageur (mitigation F-M1 + F-M2 plan).
 *
 * @param {object} props
 * @param {'caution'|'sejour'} props.kind
 * @param {string} props.paiementUrl — URL FBZ courte (https://sejour.fullbnbzen.com/paiement/{token})
 * @param {string|null} props.expiresAtIso — Token form_tokens.expires_at (ISO 8601)
 * @param {boolean} props.canRegenerate — false si token actif (UX hint)
 * @param {boolean} props.busy — affichage spinner pendant action
 * @param {function} props.onCopy — handler copier (toast feedback)
 * @param {function} props.onRegenerate — handler régénérer
 * @param {boolean} props.stripeConfigured — Stripe configuré (sinon disabled)
 * @param {number|null} [props.nbNuits] — nuits du séjour, kind='caution' seul :
 *   décide empreinte vs débit réel dans le texte d'aide (cf. cautionLinkHint).
 * @param {{figee: object|null, log: object|null}|null} [props.cautionOpts] —
 *   P-2.56 : ligne (colonnes de gel) et logement, pour le seuil réel.
 */
function CheckoutLinkBox({
  kind,
  paiementUrl,
  expiresAtIso,
  canRegenerate,
  busy,
  onCopy,
  onRegenerate,
  stripeConfigured,
  nbNuits = null,
  cautionOpts = null,
}) {
  if (!paiementUrl) return null
  // C28 : on affiche la durée du token FBZ (jusqu'à check-in+30j, Q-D1 C),
  // pas la durée Stripe Session (créée à la demande, 23h max).
  const expiresLabel = expiresAtIso
    ? `Valable jusqu'au ${formatDateTimeFR(expiresAtIso)}`
    : null
  return (
    <div
      data-testid={`paiements-link-display-${kind}`}
      style={{
        marginTop: 12,
        padding: "10px 12px",
        background: DS.cielSoft,
        border: `1px solid 66`,
        borderRadius: 8,
        fontSize: 11,
        lineHeight: 1.5,
      }}
    >
      <div style={{ marginBottom: 8, fontWeight: 700, color: DS.navy, fontSize: 12 }}>
        Lien paiement sécurisé FBZ
      </div>
      <div style={{ marginBottom: 8 }}>
        <a
          href={paiementUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`paiements-link-url-${kind}`}
          style={{
            color: DS.navy,
            wordBreak: "break-all",
            textDecoration: "underline",
            fontFamily: "monospace",
            fontSize: 10,
          }}
        >
          {paiementUrl}
        </a>
      </div>
      {expiresLabel && (
        <div style={{ marginBottom: 8, color: "#666", fontSize: 11 }}>{expiresLabel}</div>
      )}
      {/* Aide contextuelle caution — TEXTE DE LITIGE, dérivé de la MÊME règle
          que la décision Stripe (cautionModeForNights). Avant : « pré-autorisation
          30 jours sur sa CB », qui confondait la validité du lien FBZ (30 j) avec
          le hold Stripe (~7 j) et ignorait le débit réel au-delà de 4 nuits. */}
      {kind === "caution" && (
        <div
          data-testid="paiements-link-hint-caution"
          style={{ marginBottom: 8, fontSize: 11, color: "#666", lineHeight: 1.5 }}
        >
          {cautionLinkHint(nbNuits, cautionOpts).text}
        </div>
      )}
      {kind === "sejour" && (
        <div
          data-testid="paiements-link-hint-sejour"
          style={{ marginBottom: 8, fontSize: 11, color: "#666", lineHeight: 1.5 }}
        >
          Page FBZ brandée. Charge immédiate à la finalisation par le voyageur.
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onCopy}
          disabled={busy}
          data-testid={`paiements-link-copy-${kind}`}
          style={{
            background: DS.cielSoft,
            color: DS.navy,
            border: `0.5px solid `,
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 11,
            fontWeight: 600,
            cursor: busy ? "wait" : "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          Copier le lien
        </button>
        <button
          type="button"
          onClick={onRegenerate}
          disabled={!stripeConfigured || busy || !canRegenerate}
          title={
            !stripeConfigured
              ? "Logement non configuré Stripe"
              : !canRegenerate
                ? "Session encore active, attendre expiration ou utilisation"
                : "Générer un nouveau lien (invalide l'ancien)"
          }
          data-testid={`paiements-link-regenerate-${kind}`}
          style={{
            background: canRegenerate && stripeConfigured ? "#facc15" : "#cbd5e1",
            color: canRegenerate && stripeConfigured ? "#1c1917" : "#fff",
            border: "none",
            borderRadius: 6,
            padding: "6px 10px",
            fontSize: 11,
            fontWeight: 700,
            cursor: !stripeConfigured || busy || !canRegenerate ? "not-allowed" : "pointer",
            opacity: canRegenerate && stripeConfigured ? 1 : 0.55,
            fontFamily: DS.fontBody,
          }}
        >
          Régénérer
        </button>
      </div>
    </div>
  )
}

// ── Helper API call ─────────────────────────────────────────────────

async function callStripeAction(action, body) {
  const { data: { session } } = await supabase.auth.getSession()
  if (!session?.access_token) throw new Error("Non authentifié")
  const res = await fetch(`/api/stripe?action=${action}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(body),
  })
  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(json.message || json.error || `http_${res.status}`)
  }
  return json
}

// ── Body principal ──────────────────────────────────────────────────

/**
 * Body PaiementsTab Stripe-centric.
 *
 * @param {object} props
 * @param {object} props.r — reservation-shape (résa OU pricing_block mapped)
 * @param {'reservation'|'pricing_block'} props.itemKind
 * @param {object|null} props.cfg — config FBZ (lookup stripe_account_id)
 * @param {function} props.onUpdated — callback refresh parent après action
 * @param {function} [props.onBookingCancelled] — callback après annulation
 *   RÉUSSIE d'une résa directe (bouton « Annuler la réservation ») : fermeture
 *   + refresh calendrier côté ResaV2Modal. Fallback : onUpdated.
 */
export default function PaiementsTabBody({ r, itemKind, cfg, onUpdated, onBookingCancelled }) {
  const isBlock = itemKind === "pricing_block"
  const isAirbnb = !isBlock && r.source === "airbnb"
  const isBooking = !isBlock && r.source === "booking"
  // Direct = reservation source=direct OU pricing_block (résa interne FBZ).
  const isDirect = isBlock || (!isBlock && r.source === "direct")

  // Hooks (déclarés tous en tête — rules-of-hooks : ne pas appeler conditionnel.
  // L'early-return Airbnb plus bas n'utilise pas ces hooks mais on les
  // déclare quand même pour rester linéaire à chaque render).
  // Résolution stripe_account_id (UI defensive L-1).
  const stripeAccountId = useMemo(
    () => resolveStripeAccountIdClient(cfg, r.logement_id),
    [cfg, r.logement_id],
  )
  // États action buttons.
  const [busy, setBusy] = useState(null) // 'caution_resend' | 'sejour_resend' | 'release' | 'capture'
  const [actionErr, setActionErr] = useState(null)
  const [actionOk, setActionOk] = useState(null)
  const [confirmReleaseOpen, setConfirmReleaseOpen] = useState(false)
  // Fix gardes release : motif obligatoire (min 20 car.), miroir serveur.
  const [releaseRaison, setReleaseRaison] = useState("")

  // C43 — états modal capture caution (double-confirm Cap1).
  const [confirmCaptureOpen, setConfirmCaptureOpen] = useState(false)
  const [captureStep, setCaptureStep] = useState(0) // 0 = saisie, 1 = confirmation
  const [captureAmount, setCaptureAmount] = useState("")
  const [captureRaison, setCaptureRaison] = useState("")
  const [captureVoyageurNotified, setCaptureVoyageurNotified] = useState(false)

  // Lot annulation résa payée (2e accès UI calendrier) — modale partagée.
  // Ouverte au clic uniquement (le fetch booking_cancel_info se fait DANS la
  // modale à l'ouverture — aucun appel superflu pour les blocs non concernés).
  const [cancelBookingOpen, setCancelBookingOpen] = useState(false)

  // ADR-174 PR4a — états modal remboursement partiel (double-confirm argent).
  const [confirmRefundOpen, setConfirmRefundOpen] = useState(false)
  const [refundStep, setRefundStep] = useState(0) // 0 = saisie, 1 = confirmation
  const [refundAmount, setRefundAmount] = useState("")
  const [refundRaison, setRefundRaison] = useState("")
  const [refundVoyageurNotified, setRefundVoyageurNotified] = useState(false)

  // C28 — tokens form_tokens actifs (lookup direct supabase, RLS admin policy).
  // 2 tokens trackés : payment_caution + payment_sejour.
  // refreshTrigger : compteur incrémenté après handleResend* pour re-fetch.
  const [refreshTrigger, setRefreshTrigger] = useState(0)
  const [cautionToken, setCautionToken] = useState(null) // { token, expires_at } | null
  const [sejourToken, setSejourToken] = useState(null)

  useEffect(() => {
    if (!r?.id) return
    let aborted = false
    ;(async () => {
      try {
        const { data } = await supabase
          .from("form_tokens")
          .select("token, type, expires_at, used_at")
          .eq("reservation_id", r.id)
          .in("type", ["payment_caution", "payment_sejour"])
          .is("used_at", null)
          .gt("expires_at", new Date().toISOString())
          .order("created_at", { ascending: false })
        if (aborted) return
        const rows = Array.isArray(data) ? data : []
        const caution = rows.find((t) => t.type === "payment_caution") || null
        const sejour = rows.find((t) => t.type === "payment_sejour") || null
        setCautionToken(caution)
        setSejourToken(sejour)
      } catch (_) {
        // silent (RLS peut refuser pour non-admin — UI bénin).
      }
    })()
    return () => {
      aborted = true
    }
  }, [r?.id, refreshTrigger])

  // P-2.56 — seuil et mode que le serveur appliquera à CE lien de caution
  // (caution figée, sur la demande pour un direct) : lus par la même porte que
  // la page de paiement (token_resolve, lecture seule). Inconnus → calcul local.
  const [cautionServeur, setCautionServeur] = useState(null)
  useEffect(() => {
    const tok = cautionToken?.token
    if (!tok) {
      setCautionServeur(null)
      return
    }
    let aborted = false
    ;(async () => {
      try {
        const res = await fetch(`/api/stripe?action=token_resolve&token=${encodeURIComponent(tok)}`)
        const json = await res.json().catch(() => ({}))
        if (aborted || !res.ok) return
        setCautionServeur({
          mode: json.caution_kind === "long_sejour" ? "debit" : "empreinte",
          seuil: json.caution_seuil_nuits,
        })
      } catch (e) {
        console.error("[paiements] seuil de caution illisible", e && e.message)
      }
    })()
    return () => {
      aborted = true
    }
  }, [cautionToken?.token])

  // Cas Airbnb : section read-only AirCover (inchangé v1.4).
  if (isAirbnb) {
    return (
      <div data-testid="resa-modal-paiements-body">
        <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 16 }}>
          Statut paiement & caution
        </div>
        <div style={{ padding: "12px 14px", background: DS.cielSoft, color: "#1565C0", borderRadius: 8, fontSize: 12, marginBottom: 20, lineHeight: 1.5 }}>
          Cette réservation est sur Airbnb — paiements et caution gérés par la plateforme directement, aucune action manuelle requise.
        </div>
        <div className="fbz-grid-collapse" style={{ gap: 16 }}>
          <div data-testid="resa-modal-airbnb-payment-readonly" style={{ background: DS.white, border: `1px solid `, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DS.navy, marginBottom: 10 }}>Paiement</div>
            <div style={{ fontSize: 13, color: DS.green, fontWeight: 700, marginBottom: 6 }}>Payé via Airbnb</div>
            <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.5 }}>
              Versement automatique par Airbnb 24h après l'arrivée du voyageur.
            </div>
          </div>
          <div data-testid="resa-modal-airbnb-caution-readonly" style={{ background: DS.white, border: `1px solid `, borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DS.navy, marginBottom: 10 }}>Caution</div>
            <div style={{ fontSize: 13, color: DS.green, fontWeight: 700, marginBottom: 6 }}>Géré via AirCover</div>
            <div style={{ fontSize: 11, color: DS.textMuted, lineHeight: 1.5 }}>
              Protection AirCover incluse — pas de caution manuelle à collecter.
            </div>
          </div>
        </div>
        <div style={{ marginTop: 20, fontSize: 11, color: DS.textMuted, lineHeight: 1.5, padding: "10px 12px", background: DS.card, borderRadius: 8 }}>
          Airbnb gère intégralement le règlement et la couverture des dommages — aucune intervention requise côté FBZ.
        </div>
      </div>
    )
  }

  // Booking / Direct / pricing_block — section Stripe interactive.
  const stripeConfigured = !!stripeAccountId

  const cautionStatus = r.caution_status || "pending"
  const cautionAmount = r.caution_amount_eur != null ? Number(r.caution_amount_eur) : null
  const cautionPiId = r.caution_payment_intent_id || null

  const paiementStatus = r.paiement_sejour_status || (r.paid_at ? "succeeded" : "pending")
  const paiementPiId = r.paiement_sejour_intent_id || null

  // Matrix états boutons (cf. plan v1.3 §3).
  const cautionResendEnabled =
    stripeConfigured &&
    (cautionStatus === "pending" || cautionStatus === "requires_payment_method")
  const sejourResendEnabled =
    stripeConfigured && isDirect && (paiementStatus === "pending" || paiementStatus === "failed")
  const releaseEnabled = stripeConfigured && cautionStatus === "authorized"
  const captureEnabled = stripeConfigured && cautionStatus === "authorized" && !!cautionPiId
  // ADR-174 PR4a : rembourser tout/partie d'une caution encaissée (argent détenu).
  const refundEnabled =
    stripeConfigured &&
    !!cautionPiId &&
    (cautionStatus === "captured" ||
      cautionStatus === "charged" ||
      cautionStatus === "partially_refunded")
  // Lien Dashboard externe : disponible dès qu'il y a un PI (même captured/released).
  const dashboardEnabled = stripeConfigured && !!cautionPiId

  // C12.6 fix : Stripe Dashboard URL test vs live mode detection.
  // PI live commence par pi_live_, sinon mode test = /test/payments/.
  const isLivePI = (cautionPiId || "").startsWith("pi_live_")
  const dashboardPath = isLivePI ? "/payments/" : "/test/payments/"
  const dashboardUrl = cautionPiId
    ? `https://dashboard.stripe.com${dashboardPath}${encodeURIComponent(cautionPiId)}`
    : null

  // C28 pivot Embedded : URL FBZ courte construite depuis form_tokens.
  // Hide auto si : authorized OU PI présent OU token NULL.
  const appBase = buildAppBaseUrl()
  const cautionPaiementUrl = cautionToken?.token
    ? `${appBase}/paiement/${cautionToken.token}`
    : null
  const cautionShowSessionBox =
    !!cautionPaiementUrl &&
    cautionStatus !== "authorized" &&
    cautionStatus !== "captured" &&
    cautionStatus !== "released" &&
    cautionStatus !== "refunded" &&
    !cautionPiId // proxy "token consumed" pre-webhook downstream

  // Régénération toujours possible (admin force nouveau token via regenerate=true).
  const cautionCanRegenerate = stripeConfigured

  const sejourPaiementUrl = sejourToken?.token
    ? `${appBase}/paiement/${sejourToken.token}`
    : null
  const sejourShowSessionBox =
    !!sejourPaiementUrl &&
    paiementStatus !== "succeeded" &&
    paiementStatus !== "refunded" &&
    !paiementPiId
  const sejourCanRegenerate = stripeConfigured

  // Actions handlers.
  async function handleResendCaution() {
    setBusy("caution_resend"); setActionErr(null); setActionOk(null)
    try {
      const json = await callStripeAction("create_caution", { reservation_id: r.id })
      setActionOk(
        json.already_created
          ? "Lien caution déjà existant (réutilisé)"
          : "Lien caution généré",
      )
      // C28 : trigger re-fetch tokens form_tokens (UI fraîche).
      setRefreshTrigger((n) => n + 1)
      onUpdated && onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  async function handleResendSejour() {
    setBusy("sejour_resend"); setActionErr(null); setActionOk(null)
    try {
      const json = await callStripeAction("create_paiement_sejour", { reservation_id: r.id })
      setActionOk(
        json.already_created
          ? "Lien séjour déjà existant (réutilisé)"
          : "Lien séjour généré",
      )
      setRefreshTrigger((n) => n + 1)
      onUpdated && onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  // C12 Checkout Session : copier URL + régénérer (toast feedback).
  async function handleCopyLink(url) {
    setActionErr(null); setActionOk(null)
    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
        setActionOk("Lien copié dans le presse-papier")
      } else {
        setActionErr("Clipboard API indisponible (navigateur)")
      }
    } catch (e) {
      setActionErr(`Copie échouée: ${e.message}`)
    }
  }

  async function handleRegenerateCaution() {
    setBusy("caution_regenerate"); setActionErr(null); setActionOk(null)
    try {
      await callStripeAction("create_caution", { reservation_id: r.id, regenerate: true })
      setActionOk("Régénéré avec nouveau lien")
      setRefreshTrigger((n) => n + 1)
      if (onUpdated) onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  async function handleRegenerateSejour() {
    setBusy("sejour_regenerate"); setActionErr(null); setActionOk(null)
    try {
      await callStripeAction("create_paiement_sejour", { reservation_id: r.id, regenerate: true })
      setActionOk("Régénéré avec nouveau lien")
      setRefreshTrigger((n) => n + 1)
      onUpdated && onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  function openReleaseConfirm() {
    setActionErr(null); setActionOk(null)
    setReleaseRaison("")
    setConfirmReleaseOpen(true)
  }

  // C43 — handlers modal capture caution
  function openCaptureConfirm() {
    setActionErr(null); setActionOk(null)
    setCaptureStep(0)
    // Pré-remplit avec le montant max (caution_amount_eur). Cap4 : input libre.
    setCaptureAmount(cautionAmount != null ? String(cautionAmount) : "")
    setCaptureRaison("")
    setCaptureVoyageurNotified(false)
    setConfirmCaptureOpen(true)
  }

  function closeCaptureConfirm() {
    if (busy === "capture") return // anti-clic pendant POST
    setConfirmCaptureOpen(false)
    setCaptureStep(0)
  }

  function nextCaptureStep() {
    setCaptureStep(1)
  }

  function backCaptureStep() {
    setCaptureStep(0)
  }

  async function handleCaptureConfirmed() {
    // Validation client (mirror serveur, defense-in-depth ADR-094).
    const amountNum = Number(captureAmount)
    const raisonTrim = (captureRaison || "").trim()
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setActionErr("Montant invalide")
      return
    }
    if (cautionAmount != null && amountNum > cautionAmount) {
      setActionErr(`Montant > caution autorisée (${cautionAmount.toFixed(2)} €)`)
      return
    }
    if (raisonTrim.length < 20) {
      setActionErr("Raison trop courte (min 20 caractères)")
      return
    }
    if (captureVoyageurNotified !== true) {
      setActionErr("Confirmer que le voyageur a été informé")
      return
    }

    setBusy("capture"); setActionErr(null); setActionOk(null)
    try {
      const json = await callStripeAction("capture_caution", {
        reservation_id: r.id,
        amount_eur: amountNum,
        raison: raisonTrim,
        voyageur_notified: true,
      })
      // Cap7 : optimistic refresh.
      const okMsg = json.email_sent
        ? `Capture validée — ${amountNum.toFixed(2)} € débités, email proprio envoyé`
        : `Capture validée — ${amountNum.toFixed(2)} € débités (email proprio : non envoyé)`
      setActionOk(okMsg)
      setConfirmCaptureOpen(false)
      setCaptureStep(0)
      // Reset states.
      setCaptureAmount("")
      setCaptureRaison("")
      setCaptureVoyageurNotified(false)
      if (onUpdated) onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  async function handleReleaseConfirmed() {
    // Validation client (miroir serveur, defense-in-depth ADR-094) : la
    // libération est définitive côté Stripe → motif obligatoire, tracé.
    const raisonTrim = (releaseRaison || "").trim()
    if (raisonTrim.length < 20) {
      setActionErr("Motif trop court (min 20 caractères)")
      return
    }
    setBusy("release"); setActionErr(null); setActionOk(null)
    try {
      await callStripeAction("resolve_caution", {
        reservation_id: r.id,
        mode: "release",
        raison: raisonTrim,
      })
      setActionOk(`Libération demandée — webhook Stripe propagera caution_status='released'`)
      setConfirmReleaseOpen(false)
      if (onUpdated) onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  // ADR-174 PR4a — handlers remboursement partiel (double-confirm).
  function openRefundConfirm() {
    setActionErr(null); setActionOk(null)
    setRefundStep(0)
    setRefundAmount("") // pas de pré-remplissage : l'admin saisit le montant à RENDRE
    setRefundRaison("")
    setRefundVoyageurNotified(false)
    setConfirmRefundOpen(true)
  }

  function closeRefundConfirm() {
    if (busy === "refund") return // anti-clic pendant POST
    setConfirmRefundOpen(false)
    setRefundStep(0)
  }

  async function handleRefundConfirmed() {
    const amountNum = Number(refundAmount)
    const raisonTrim = (refundRaison || "").trim()
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setActionErr("Montant invalide")
      return
    }
    if (cautionAmount != null && amountNum > cautionAmount) {
      setActionErr(`Montant > caution (${cautionAmount.toFixed(2)} €)`)
      return
    }
    if (raisonTrim.length < 10) {
      setActionErr("Raison trop courte (min 10 caractères)")
      return
    }
    if (refundVoyageurNotified !== true) {
      setActionErr("Confirmer que le voyageur a été informé du remboursement")
      return
    }
    setBusy("refund"); setActionErr(null); setActionOk(null)
    try {
      const json = await callStripeAction("refund_caution_partial", {
        reservation_id: r.id,
        amount_eur: amountNum,
        raison: raisonTrim,
        voyageur_notified: true,
      })
      const statutLabel = json.caution_status === "refunded" ? "remboursé en totalité" : "remboursé partiellement"
      // 202 : soumis à Stripe, PAS confirmé — on ne dit pas « rendus » (décision
      // Dimitri 2026-09-15). Le serveur fournit la phrase exacte.
      setActionOk(json.pending ? (json.message || "Remboursement soumis, en attente de confirmation Stripe.") : `Remboursement validé — ${amountNum.toFixed(2)} € rendus (${statutLabel})`)
      setConfirmRefundOpen(false)
      setRefundStep(0)
      setRefundAmount("")
      setRefundRaison("")
      setRefundVoyageurNotified(false)
      if (onUpdated) onUpdated()
    } catch (e) {
      setActionErr(e.message)
      // Revue adverse #1419 (CRITIQUE-1) : après un échec — dont une réponse
      // PERDUE alors que Stripe a remboursé — la modale restait ouverte, le
      // double-confirm déjà franchi, et un re-clic repartait sur un statut
      // périmé. On referme, on remet la saisie à zéro et on RELIT la fiche :
      // le prochain geste part d'un statut frais, et le serveur refuse de
      // toute façon un doublon récent à voix haute.
      setConfirmRefundOpen(false)
      setRefundStep(0)
      setRefundVoyageurNotified(false)
      if (onUpdated) onUpdated()
    } finally {
      setBusy(null)
    }
  }

  // ADR-174 PR4b — annulation d'un remboursement auto programmé (J+2).
  // Ne déplace pas d'argent (stoppe juste le refund auto) → simple confirm.
  async function handleCancelScheduledRefund() {
    if (
      !window.confirm(
        "Annuler le remboursement automatique programmé ?\n\nLa caution restera encaissée — tu devras la traiter manuellement (capturer le dégât / rembourser le reste).",
      )
    )
      return
    setBusy("cancel_refund"); setActionErr(null); setActionOk(null)
    try {
      await callStripeAction("cancel_scheduled_refund", { reservation_id: r.id })
      setActionOk("Remboursement programmé annulé — action manuelle requise.")
      if (onUpdated) onUpdated()
    } catch (e) {
      setActionErr(e.message)
    } finally {
      setBusy(null)
    }
  }

  // Bandeau source (Booking vs Direct).
  const sourceLabel = isBlock ? "Direct interne (pricing_block)" : (r.source || "")

  // Lot annulation résa payée — bouton visible UNIQUEMENT pour un bloc
  // direct-booking (marqueur provenance `created_by='checkout'`, propagé par
  // mapBlockToReservationShape en `_created_by`) encore actif. Blocage
  // manuel/OTA (pas de demande liée) → rien d'affiché, zéro requête. La
  // vérification d'annulabilité fine (statut demande, séjour commencé) reste
  // au serveur : booking_cancel_info, fetchée à l'ouverture de la modale.
  //
  // MINEUR-1 (pessimist #994) — filtre client anti-bruit : un bloc checkout en
  // HOLD non payé (demande validated/signed, paiement pas encore fait) est
  // actif + created_by='checkout' mais N'EST PAS annulable (sa libération =
  // expiration auto du hold). On exige un signal de paiement abouti, en parité
  // avec CANCELLABLE_STATUSES serveur (paid_stay / paid_caution / confirmed) :
  // séjour payé (succeeded / paid_at) OU caution posée (authorized / charged,
  // couvre paid_caution avant paiement séjour). Filtre UX seulement — la garde
  // qui fait foi reste booking_cancel_info côté serveur.
  const blockHasPaidSignal =
    r.paiement_sejour_status === "succeeded" ||
    r.paid_at != null ||
    r.caution_status === "authorized" ||
    r.caution_status === "charged"
  const cancelBookingVisible =
    isBlock && r._created_by === "checkout" && r._active !== false &&
    blockHasPaidSignal
  const cancelLogementNom = (() => {
    const log = findLogementInCfg(cfg, r.logement_id)
    return (log && log.nom) || r.logement_id
  })()

  // Contexte de l'aperçu #21 « caution restituée » (mécanique B) : nb nuits
  // (axe empreinte/débit), prénom voyageur, nom logement. Le montant prélevé/
  // libéré et le motif sont fournis par chaque modale (valeurs LIVE).
  // Calcul DIRECT (pas de useMemo) : ce point du composant est situé après des
  // retours anticipés → un hook ici viole les règles des hooks React (build CRA
  // en erreur, et rendu cassé « Rendered more hooks than during the previous
  // render »). Le calcul est trivial (2 dates + 2 champs), même style que
  // cancelLogementNom juste au-dessus.
  const cautionPreviewCtx = (() => {
    let nbNuits = null
    if (r.start_date && r.end_date) {
      const ms = new Date(r.end_date).getTime() - new Date(r.start_date).getTime()
      if (Number.isFinite(ms) && ms > 0) nbNuits = Math.round(ms / 86400000)
    }
    return {
      nbNuits,
      guestFirstName: r.guest_first_name || "",
      logementName: cancelLogementNom,
    }
  })()

  return (
    <div data-testid="resa-modal-paiements-body">
      <div style={{ fontFamily: DS.fontDisplay, fontSize: 16, fontWeight: 700, color: DS.navy, marginBottom: 16 }}>
        Statut paiement & caution
      </div>

      {/* Bandeau warning stripe non configuré (L-1 defensive). */}
      {!stripeConfigured && (
        <div
          data-testid="paiements-no-stripe-warning"
          style={{ padding: "12px 14px", background: DS.warningBg, color: "#E65100", borderRadius: 8, fontSize: 12, marginBottom: 16, lineHeight: 1.5 }}
        >
          Logement <strong>{r.logement_id}</strong> non configuré Stripe (stripe_account_id absent).
          Toutes les actions sont désactivées. Configurer via onglet Info Logement → Stripe.
        </div>
      )}

      <div style={{ padding: "10px 12px", background: DS.pageBg, color: "#444", borderRadius: 8, fontSize: 11, marginBottom: 18, lineHeight: 1.5 }}>
        Source : <strong>{sourceLabel}</strong>
        {isBooking && (
          <> · Caution Stripe manuelle. Paiement séjour <strong>via Booking</strong> (pas de PI séjour FBZ).</>
        )}
        {isDirect && (
          <> · Caution + Paiement séjour via Stripe (pattern agency-only).</>
        )}
      </div>

      {/* ── Statut Caution ───────────────────────────────────────── */}
      <div
        data-testid="paiements-section-caution"
        style={{ background: DS.white, border: `1px solid `, borderRadius: 12, padding: 20, marginBottom: 16 }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: DS.navy }}>Caution</div>
          <StatusBadge status={cautionStatus} labelMap={CAUTION_BADGES} />
        </div>
        <div style={{ fontSize: 12, color: "#444", marginBottom: 4 }}>
          Montant :{" "}
          <strong>{cautionAmount != null ? `${cautionAmount.toFixed(2)} €` : "—"}</strong>
        </div>
        {cautionPiId && (
          <div style={{ fontSize: 11, color: DS.textMuted, marginBottom: 4, fontFamily: "monospace" }}>
            PI : {cautionPiId}
          </div>
        )}
        {/* Boutons actions Caution */}
        <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            data-testid="paiements-btn-resend-caution"
            onClick={handleResendCaution}
            disabled={!cautionResendEnabled || busy !== null}
            title={!stripeConfigured ? "Logement non configuré Stripe" : (!cautionResendEnabled ? "Disponible uniquement si statut = en attente" : undefined)}
            style={{
              background: cautionResendEnabled ? DS.navy : "#cbd5e1",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: cautionResendEnabled && !busy ? "pointer" : "not-allowed",
              opacity: cautionResendEnabled ? 1 : 0.55,
              fontFamily: DS.fontBody,
            }}
          >
            {busy === "caution_resend" ? "…" : "Renvoyer lien caution"}
          </button>
          <button
            data-testid="paiements-btn-release-caution"
            onClick={openReleaseConfirm}
            disabled={!releaseEnabled || busy !== null}
            title={!stripeConfigured ? "Logement non configuré Stripe" : (!releaseEnabled ? "Disponible uniquement si statut = pré-autorisée" : undefined)}
            style={{
              background: releaseEnabled ? DS.green : "#cbd5e1",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: releaseEnabled && !busy ? "pointer" : "not-allowed",
              opacity: releaseEnabled ? 1 : 0.55,
              fontFamily: DS.fontBody,
            }}
          >
            Libérer maintenant
          </button>
          <button
            data-testid="paiements-btn-capture-caution"
            onClick={openCaptureConfirm}
            disabled={!captureEnabled || busy !== null}
            title={!stripeConfigured
              ? "Logement non configuré Stripe"
              : (!captureEnabled ? "Capture disponible uniquement si statut = pré-autorisée" : "Capturer la caution (saisir détails)")}
            style={{
              background: captureEnabled ? DS.red : "#cbd5e1",
              color: "#fff",
              border: "none",
              borderRadius: 7,
              padding: "8px 14px",
              fontSize: 12,
              fontWeight: 700,
              cursor: captureEnabled && !busy ? "pointer" : "not-allowed",
              opacity: captureEnabled ? 1 : 0.55,
              fontFamily: DS.fontBody,
            }}
          >
            Capturer (saisir détails)
          </button>
          {/* ADR-174 PR4a — rembourser tout/partie d'une caution encaissée */}
          {refundEnabled && (
            <button
              data-testid="paiements-btn-refund-caution"
              onClick={openRefundConfirm}
              disabled={busy !== null}
              title="Rendre tout ou partie de la caution encaissée au voyageur"
              style={{
                background: DS.navy,
                color: "#fff",
                border: "none",
                borderRadius: 7,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.55 : 1,
                fontFamily: DS.fontBody,
              }}
            >
              ↩️ Rembourser une partie
            </button>
          )}
          {/* F-MOY-4 : Stripe Dashboard URL conservé en bouton diagnostic
              (lien externe inchangé, visible dès qu'il y a un PI). */}
          {dashboardEnabled && (
            <a
              data-testid="paiements-link-dashboard"
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background: DS.cielSoft,
                color: DS.navy,
                border: `0.5px solid `,
                borderRadius: 7,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 600,
                textDecoration: "none",
                fontFamily: DS.fontBody,
                display: "inline-block",
              }}
            >
              Voir Stripe Dashboard
            </a>
          )}
        </div>
        {/* ADR-174 PR4b — état remboursement (programmé / remboursé / annulé) */}
        {["refund_scheduled", "refunded", "partially_refunded", "refund_cancelled", "refund_pending"].includes(cautionStatus) && (
          <div
            data-testid="paiements-refund-status"
            style={{
              marginTop: 12,
              padding: "10px 12px",
              borderRadius: 8,
              fontSize: 12,
              lineHeight: 1.5,
              background:
                cautionStatus === "refund_cancelled" ? "#fef2f2" : "#f0f9ff",
              border:
                cautionStatus === "refund_cancelled"
                  ? "1px solid #fca5a5"
                  : "1px solid #93c5fd",
              color: cautionStatus === "refund_cancelled" ? "#991b1b" : "#1e3a8a",
            }}
          >
            {cautionStatus === "refund_scheduled" && (
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span>
                  <strong>Remboursement automatique programmé</strong>
                  {r.caution_refund_scheduled_at
                    ? ` le ${new Date(r.caution_refund_scheduled_at).toLocaleDateString("fr-FR")}`
                    : ""}
                  .
                </span>
                <button
                  data-testid="paiements-btn-cancel-scheduled-refund"
                  onClick={handleCancelScheduledRefund}
                  disabled={busy !== null}
                  style={{
                    background: DS.red,
                    color: "#fff",
                    border: "none",
                    borderRadius: 7,
                    padding: "6px 12px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: busy ? "not-allowed" : "pointer",
                    opacity: busy ? 0.55 : 1,
                    fontFamily: DS.fontBody,
                  }}
                >
                  Annuler / Suspendre
                </button>
              </div>
            )}
            {cautionStatus === "refunded" && (
              <span>
                <strong>Caution remboursée.</strong>
                {r.caution_refund_stripe_id ? ` (${r.caution_refund_stripe_id})` : ""}
              </span>
            )}
            {cautionStatus === "partially_refunded" && (
              <span>↩️ <strong>Caution partiellement remboursée</strong> (excédent rendu, montant dégât conservé).</span>
            )}
            {cautionStatus === "refund_pending" && (
              <span><strong>Remboursement en cours</strong> — appel Stripe en vol (verrou). Si cet état persiste, relire la fiche ; s'il reste, c'est un incident à regarder, pas à recliquer.</span>
            )}
            {cautionStatus === "refund_cancelled" && (
              <span><strong>Remboursement annulé</strong> — action manuelle requise (capturer le dégât / rembourser une partie ci-dessus).</span>
            )}
          </div>
        )}
        {/* C28 CheckoutLinkBox caution (URL FBZ courte) */}
        {cautionShowSessionBox && (
          <CheckoutLinkBox
            kind="caution"
            paiementUrl={cautionPaiementUrl}
            expiresAtIso={cautionToken?.expires_at || null}
            canRegenerate={cautionCanRegenerate}
            busy={busy !== null}
            onCopy={() => handleCopyLink(cautionPaiementUrl)}
            onRegenerate={handleRegenerateCaution}
            stripeConfigured={stripeConfigured}
            nbNuits={cautionPreviewCtx.nbNuits}
            cautionOpts={{
              figee: r,
              log: findLogementInCfg(cfg, r.logement_id),
              serveur: cautionServeur,
            }}
          />
        )}
      </div>

      {/* ── Statut Paiement Séjour (Direct only) ──────────────────── */}
      {isDirect && (
        <div
          data-testid="paiements-section-sejour"
          style={{ background: DS.white, border: `1px solid `, borderRadius: 12, padding: 20, marginBottom: 16 }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: DS.navy }}>Paiement séjour</div>
            <StatusBadge status={paiementStatus} labelMap={PAIEMENT_BADGES} />
          </div>
          <div style={{ fontSize: 12, color: "#444", marginBottom: 4 }}>
            Montant :{" "}
            <strong>{r.total_amount != null ? `${Number(r.total_amount).toFixed(2)} €` : "—"}</strong>
          </div>
          {paiementPiId && (
            <div style={{ fontSize: 11, color: DS.textMuted, marginBottom: 4, fontFamily: "monospace" }}>
              PI : {paiementPiId}
            </div>
          )}
          {r.paid_at && (
            <div style={{ fontSize: 11, color: DS.green, marginBottom: 4 }}>
              Payé le {formatDateTimeFR(r.paid_at)}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <button
              data-testid="paiements-btn-resend-sejour"
              onClick={handleResendSejour}
              disabled={!sejourResendEnabled || busy !== null}
              title={!stripeConfigured ? "Logement non configuré Stripe" : (!sejourResendEnabled ? "Disponible uniquement si statut = en attente ou échec" : undefined)}
              style={{
                background: sejourResendEnabled ? DS.navy : "#cbd5e1",
                color: "#fff",
                border: "none",
                borderRadius: 7,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: sejourResendEnabled && !busy ? "pointer" : "not-allowed",
                opacity: sejourResendEnabled ? 1 : 0.55,
                fontFamily: DS.fontBody,
              }}
            >
              {busy === "sejour_resend" ? "…" : "Renvoyer lien paiement séjour"}
            </button>
          </div>
          {/* C28 CheckoutLinkBox sejour (URL FBZ courte) */}
          {sejourShowSessionBox && (
            <CheckoutLinkBox
              kind="sejour"
              paiementUrl={sejourPaiementUrl}
              expiresAtIso={sejourToken?.expires_at || null}
              canRegenerate={sejourCanRegenerate}
              busy={busy !== null}
              onCopy={() => handleCopyLink(sejourPaiementUrl)}
              onRegenerate={handleRegenerateSejour}
              stripeConfigured={stripeConfigured}
            />
          )}
        </div>
      )}

      {/* ── Annuler la réservation (résa directe payée — modale partagée) ── */}
      {cancelBookingVisible && (
        <div
          data-testid="paiements-section-cancel-booking"
          style={{ marginBottom: 16, display: "flex", gap: 8 }}
        >
          <button
            className="fbz-press"
            data-testid="paiements-btn-cancel-booking"
            onClick={() => setCancelBookingOpen(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px',
              borderRadius: DS.radiusControl,
              border: `0.5px solid ${DS.borderHover}`,
              background: DS.card,
              color: DS.dangerText,
              fontWeight: 700,
              cursor: 'pointer',
              fontSize: 12,
              fontFamily: DS.fontBody,
            }}
          >
            <Ban size={15} strokeWidth={2.4}/> Annuler la réservation
          </button>
        </div>
      )}

      {/* Statut feedback action (success/error) */}
      {actionOk && (
        <div
          data-testid="paiements-action-ok"
          style={{ padding: "8px 12px", background: DS.successBg, color: DS.green, borderRadius: 6, fontSize: 12, marginBottom: 10 }}
        >
          {actionOk}
        </div>
      )}
      {actionErr && (
        <div
          data-testid="paiements-action-err"
          style={{ padding: "8px 12px", background: DS.dangerBg, color: DS.red, borderRadius: 6, fontSize: 12, marginBottom: 10 }}
        >
          {actionErr}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11, color: DS.textMuted, lineHeight: 1.5, padding: "10px 12px", background: DS.card, borderRadius: 8 }}>
        Pattern agency-only — Stripe-Account header per call, 0 intermédiation FBZ.
        Webhook Stripe propage les changements de statut automatiquement (source vérité Stripe).
      </div>

      <ConfirmReleaseModal
        open={confirmReleaseOpen}
        busy={busy === "release"}
        cautionAmount={cautionAmount != null ? cautionAmount : 0}
        previewCtx={cautionPreviewCtx}
        raison={releaseRaison}
        onRaisonChange={setReleaseRaison}
        onConfirm={handleReleaseConfirmed}
        onCancel={() => setConfirmReleaseOpen(false)}
      />

      {/* C43 — Modal double-confirm capture caution (Cap1 sécurité max) */}
      <ConfirmCaptureModal
        open={confirmCaptureOpen}
        step={captureStep}
        busy={busy === "capture"}
        cautionAmount={cautionAmount != null ? cautionAmount : 0}
        voyageurNom={
          [r.guest_first_name, r.guest_last_name].filter(Boolean).join(" ").trim() ||
          "Voyageur"
        }
        amount={captureAmount}
        raison={captureRaison}
        voyageurNotified={captureVoyageurNotified}
        onAmountChange={setCaptureAmount}
        onRaisonChange={setCaptureRaison}
        onVoyageurNotifiedChange={setCaptureVoyageurNotified}
        onNext={nextCaptureStep}
        onBack={backCaptureStep}
        onConfirm={handleCaptureConfirmed}
        onCancel={closeCaptureConfirm}
        previewCtx={cautionPreviewCtx}
      />
      <ConfirmRefundModal
        open={confirmRefundOpen}
        step={refundStep}
        busy={busy === "refund"}
        cautionAmount={cautionAmount != null ? cautionAmount : 0}
        amount={refundAmount}
        raison={refundRaison}
        voyageurNotified={refundVoyageurNotified}
        onAmountChange={setRefundAmount}
        onRaisonChange={setRefundRaison}
        onVoyageurNotifiedChange={setRefundVoyageurNotified}
        onNext={() => setRefundStep(1)}
        onBack={() => setRefundStep(0)}
        onConfirm={handleRefundConfirmed}
        onCancel={closeRefundConfirm}
        previewCtx={cautionPreviewCtx}
      />

      {/* Modale d'annulation partagée (mode pricingBlockId) — le pseudo
          `request` ne sert qu'au header (nom/dates), le request_id réel est
          résolu par booking_cancel_info côté serveur. */}
      {cancelBookingOpen && (
        <BookingCancelModal
          request={{
            id: null,
            guest_first_name: r.guest_first_name,
            guest_last_name: r.guest_last_name,
            start_date: r.start_date,
            end_date: r.end_date,
          }}
          pricingBlockId={r.id}
          logementNom={cancelLogementNom}
          onClose={() => setCancelBookingOpen(false)}
          onDone={() => {
            setCancelBookingOpen(false)
            if (onBookingCancelled) onBookingCancelled()
            else if (onUpdated) onUpdated()
          }}
        />
      )}
    </div>
  )
}
